/**
 * File: tutorialUtils.ts
 * Path: apps/admin/src/shared/utils/tutorialUtils.ts
 * Description: Picking the tutorials for a page, searching them across the
 * admin, naming the page, and making a tutorial's steps able to start from
 * another page (each step then opens its own page first).
 */

import { matchPath } from "react-router-dom";
import { ADMIN_PATHS, SIDEBAR_ITEMS, type AdminNavItem } from "@idk/config";
import { TUTORIALS } from "./tourContentData";
import type { TourStep, Tutorial } from "./tutorialTypes";

/** Pages that are not in the sidebar, by path pattern. */
const EXTRA_TITLES: Array<[string, string]> = [
  [ADMIN_PATHS.businesses.detail(":id"), "Business"],
  [ADMIN_PATHS.leads.find, "Find leads"],
  [ADMIN_PATHS.projects.detail(":clientId", ":jobId"), "Project"],
  [ADMIN_PATHS.carePlans.detail(":id"), "Care plan"],
  [ADMIN_PATHS.invoice(":key"), "Invoice"],
  [ADMIN_PATHS.contracts.detail(":id"), "Contract"],
  [ADMIN_PATHS.contracts.import, "Add a signed contract"],
  [ADMIN_PATHS.quotes.new, "New quote"],
  [ADMIN_PATHS.quotes.edit(":id"), "Edit quote"],
  [ADMIN_PATHS.quotes.estimate, "Send an estimate"],
  [ADMIN_PATHS.portfolio.new, "New case study"],
  [ADMIN_PATHS.portfolio.edit(":id"), "Case study"],
  [ADMIN_PATHS.academy.new, "New article"],
  [ADMIN_PATHS.academy.edit(":id"), "Article"],
  [ADMIN_PATHS.services.detail(":id"), "Service"],
  [ADMIN_PATHS.numbers.glossary, "Glossary"],
  [ADMIN_PATHS.tutorials, "All tutorials"],
  [ADMIN_PATHS.referrals.root, "Referrals"],
  [ADMIN_PATHS.accounting.taxIncome, "Income tax"],
  [ADMIN_PATHS.accounting.taxProvisional, "Provisional tax"],
  [ADMIN_PATHS.accounting.taxOther, "Other taxes"],
  [ADMIN_PATHS.accounting.taxCompany, "Company filings"],
  [ADMIN_PATHS.menu.crm, "CRM"],
  [ADMIN_PATHS.menu.accounting, "Accounting"],
  [ADMIN_PATHS.menu.cms, "CMS"],
];

const leaves = (items: AdminNavItem[]): AdminNavItem[] => items.flatMap((item) => (item.children ? leaves(item.children) : [item]));

const withoutQuery = (href: string) => href.split("?")[0] ?? href;

const TITLES: Array<[string, string]> = [
  ...leaves(SIDEBAR_ITEMS).flatMap((item): Array<[string, string]> => (item.href ? [[withoutQuery(item.href), item.label]] : [])),
  ...EXTRA_TITLES,
];

export const matchesPage = (pattern: string, pathname: string): boolean => matchPath({ path: pattern, end: true }, pathname) !== null;

/** The page's name for the Help dialog: "Pipeline", "Client". */
export const pageTitleFor = (pathname: string): string => TITLES.find(([pattern]) => matchesPage(pattern, pathname))?.[1] ?? "this page";

/** Every path pattern the tutorials and nav know, so a test can check coverage. */
export const knownPagePatterns = (): string[] => TITLES.map(([pattern]) => pattern);

/** The tutorials that belong to the page being shown. */
export const tutorialsForPage = (pathname: string, tutorials: Tutorial[] = TUTORIALS): Tutorial[] =>
  tutorials.filter((t) => t.pages.some((page) => matchesPage(page, pathname)));

/** A tutorial can start from anywhere when it opens a plain page; a pattern like /leads/:id needs you on one. */
const startPage = (tutorial: Tutorial): string | null => {
  const first = tutorial.pages[0];
  return first && !first.includes(":") ? first : null;
};

export const canStartFrom = (tutorial: Tutorial, pathname: string): boolean =>
  tutorial.pages.some((page) => matchesPage(page, pathname)) || startPage(tutorial) !== null;

/** Search across every tutorial in the admin: title, hint and group. Empty text matches nothing. */
export const searchTutorials = (query: string, pathname: string, tutorials: Tutorial[] = TUTORIALS): Tutorial[] => {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  return tutorials.filter((t) => {
    const haystack = `${t.title} ${t.hint} ${t.group}`.toLowerCase();
    return words.every((word) => haystack.includes(word)) && canStartFrom(t, pathname);
  });
};

/** The steps to run: from another page, each step opens its own page first. */
export const stepsToRun = (tutorial: Tutorial, pathname: string): TourStep[] => {
  if (tutorial.pages.some((page) => matchesPage(page, pathname))) return tutorial.steps;
  const page = startPage(tutorial);
  return page ? tutorial.steps.map((step) => ({ ...step, path: step.path ?? page })) : tutorial.steps;
};

/** Tutorials under their group headings, in the order the groups first appear. */
export const groupTutorials = (tutorials: Tutorial[]): Array<[string, Tutorial[]]> => {
  const groups = new Map<string, Tutorial[]>();
  for (const t of tutorials) groups.set(t.group, [...(groups.get(t.group) ?? []), t]);
  return [...groups.entries()];
};
